import { useEffect, useState } from "react";
import axios from "axios";
import { Navigate } from "react-router-dom";
import { LogOut, Download, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "@/auth";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = ["New", "Contacted", "Won", "Lost"];
const STATUS_STYLES = {
  New: "text-[#3A53A3] bg-[#3A53A3]/10",
  Contacted: "text-[#F48220] bg-[#F48220]/10",
  Won: "text-emerald-600 bg-emerald-500/10",
  Lost: "text-black/40 bg-black/5",
};

function toCSV(rows) {
  const cols = ["status", "name", "email", "phone", "company", "website", "business_type", "monthly_revenue", "monthly_ad_spend", "message", "utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "created_at"];
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const head = cols.join(",");
  const body = rows.map((r) => cols.map((c) => esc(r[c])).join(",")).join("\n");
  return `${head}\n${body}`;
}

export default function Admin() {
  const { user, token, checking, logout } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pwOpen, setPwOpen] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [pwLoading, setPwLoading] = useState(false);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    if (!token) return;
    axios
      .get(`${API}/leads`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setLeads(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  if (!checking && !user) return <Navigate to="/login" replace />;

  // Derived analytics
  const counts = STATUSES.reduce((a, s) => ({ ...a, [s]: leads.filter((l) => (l.status || "New") === s).length }), {});
  const now = Date.now();
  const last7 = leads.filter((l) => now - new Date(l.created_at).getTime() < 7 * 864e5).length;
  const campaignTally = leads.reduce((a, l) => {
    const key = l.utm_campaign || l.utm_source || "Direct";
    a[key] = (a[key] || 0) + 1;
    return a;
  }, {});
  const topCampaigns = Object.entries(campaignTally).sort((a, b) => b[1] - a[1]).slice(0, 4);
  const filtered = filter === "All" ? leads : leads.filter((l) => (l.status || "New") === filter);

  const updateStatus = async (id, status) => {
    const prev = leads;
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
    try {
      await axios.patch(`${API}/leads/${id}/status`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Marked as ${status}.`);
    } catch {
      setLeads(prev);
      toast.error("Could not update status.");
    }
  };

  const exportCSV = () => {
    if (!leads.length) { toast.error("No leads to export yet."); return; }
    const blob = new Blob([toCSV(leads)], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `dna-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    toast.success("Leads exported to CSV.");
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, pw, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Password updated.");
      setPwOpen(false);
      setPw({ current_password: "", new_password: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update password.");
    } finally {
      setPwLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#FDFCF8] px-5 md:px-10 py-16" data-testid="admin-page">
      <div className="max-w-[1200px] mx-auto">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-display text-4xl font-extrabold tracking-tighter">Leads <span className="text-[#F48220]">/ {leads.length}</span></h1>
            <p className="font-body text-black/50 mt-2">Every audit request captured from the landing page.</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <button
              onClick={exportCSV}
              data-testid="export-csv-button"
              className="inline-flex items-center gap-2 rounded-full bg-[#F48220] px-5 py-2.5 font-body text-sm font-semibold text-black hover-lift"
            >
              <Download className="h-4 w-4" /> Export CSV
            </button>
            <button
              onClick={() => setPwOpen(true)}
              data-testid="open-change-password-button"
              className="inline-flex items-center gap-2 rounded-full border border-black/15 px-5 py-2.5 font-body text-sm font-semibold hover:bg-black hover:text-white transition-colors duration-300"
            >
              <KeyRound className="h-4 w-4" /> Password
            </button>
            <button
              onClick={logout}
              data-testid="logout-button"
              className="inline-flex items-center gap-2 rounded-full border border-black/15 px-5 py-2.5 font-body text-sm font-semibold hover:bg-black hover:text-white transition-colors duration-300"
            >
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          </div>
        </div>

        {/* Analytics snapshot */}
        <div className="mt-10 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="stats-snapshot">
          <StatCard label="Total leads" value={leads.length} accent="#0A0A0A" />
          <StatCard label="New" value={counts.New || 0} accent="#3A53A3" />
          <StatCard label="Contacted" value={counts.Contacted || 0} accent="#F48220" />
          <StatCard label="Won" value={counts.Won || 0} accent="#10b981" />
          <StatCard label="Lost" value={counts.Lost || 0} accent="#9ca3af" />
          <StatCard label="Last 7 days" value={last7} accent="#E70D71" />
        </div>

        {/* Top campaigns */}
        {topCampaigns.length > 0 && (
          <div className="mt-4 rounded-2xl border border-black/10 bg-white p-5" data-testid="top-campaigns">
            <div className="font-body text-xs font-bold uppercase tracking-[0.2em] text-black/40 mb-3">Top sources / campaigns</div>
            <div className="flex flex-wrap gap-2">
              {topCampaigns.map(([name, n]) => (
                <span key={name} className="inline-flex items-center gap-2 rounded-full bg-[#3A53A3]/8 px-4 py-1.5 font-body text-sm font-semibold text-[#3A53A3]">
                  {name} <span className="text-black/40">· {n}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Status filter tabs */}
        <div className="mt-8 flex flex-wrap gap-2" data-testid="status-filter">
          {["All", ...STATUSES].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              data-testid={`filter-${s.toLowerCase()}`}
              className={`rounded-full px-4 py-2 font-body text-sm font-semibold transition-colors duration-200 ${
                filter === s ? "bg-[#0A0A0A] text-white" : "border border-black/15 text-black/60 hover:border-black/40"
              }`}
            >
              {s} <span className="opacity-60">{s === "All" ? leads.length : (counts[s] || 0)}</span>
            </button>
          ))}
        </div>

        <div className="mt-6 overflow-x-auto rounded-2xl border border-black/10 bg-white">
          <table className="w-full text-sm" data-testid="leads-table">
            <thead>
              <tr className="text-left font-body text-xs uppercase tracking-wider text-black/40 border-b border-black/10">
                {["Status", "Name", "Email", "Phone", "Company", "Type", "Ad Spend", "Source", "When"].map((h) => (
                  <th key={h} className="p-4 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td className="p-6 text-black/40" colSpan={9}>Loading...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td className="p-6 text-black/40" colSpan={9}>No {filter === "All" ? "" : filter.toLowerCase() + " "}leads yet.</td></tr>
              ) : filtered.map((l) => (
                <tr key={l.id} className="border-b border-black/5 hover:bg-black/[0.02]">
                  <td className="p-3 whitespace-nowrap">
                    <Select value={l.status || "New"} onValueChange={(v) => updateStatus(l.id, v)}>
                      <SelectTrigger data-testid={`status-select-${l.id}`} className={`h-8 w-[120px] rounded-full border-0 font-semibold text-xs ${STATUS_STYLES[l.status || "New"]}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUSES.map((s) => (
                          <SelectItem key={s} value={s}>{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="p-4 font-semibold whitespace-nowrap">{l.name}</td>
                  <td className="p-4 whitespace-nowrap">{l.email}</td>
                  <td className="p-4 whitespace-nowrap">{l.phone}</td>
                  <td className="p-4 whitespace-nowrap">{l.company}</td>
                  <td className="p-4 whitespace-nowrap">{l.business_type}</td>
                  <td className="p-4 whitespace-nowrap">{l.monthly_ad_spend}</td>
                  <td className="p-4 whitespace-nowrap text-black/50">{l.utm_source ? `${l.utm_source}${l.utm_campaign ? " · " + l.utm_campaign.slice(0, 10) : ""}` : "Direct"}</td>
                  <td className="p-4 whitespace-nowrap text-black/50">{new Date(l.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent data-testid="change-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Change password</DialogTitle>
            <DialogDescription>Update your admin password. Minimum 8 characters.</DialogDescription>
          </DialogHeader>
          <form onSubmit={changePassword} className="space-y-4">
            <div className="space-y-2">
              <Label className="font-body text-sm">Current password</Label>
              <Input data-testid="current-password-input" type="password" value={pw.current_password}
                onChange={(e) => setPw((p) => ({ ...p, current_password: e.target.value }))} required />
            </div>
            <div className="space-y-2">
              <Label className="font-body text-sm">New password</Label>
              <Input data-testid="new-password-input" type="password" value={pw.new_password}
                onChange={(e) => setPw((p) => ({ ...p, new_password: e.target.value }))} required />
            </div>
            <DialogFooter>
              <button
                type="submit"
                disabled={pwLoading}
                data-testid="save-password-button"
                className="inline-flex items-center justify-center rounded-full bg-[#0A0A0A] px-6 py-2.5 font-body text-sm font-semibold text-white disabled:opacity-60 hover-lift"
              >
                {pwLoading ? "Saving..." : "Save password"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </main>
  );
}


function StatCard({ label, value, accent }) {
  return (
    <div className="rounded-2xl border border-black/10 bg-white p-5">
      <div className="font-display text-3xl font-extrabold tracking-tight" style={{ color: accent }}>{value}</div>
      <div className="mt-1 font-body text-xs text-black/50">{label}</div>
    </div>
  );
}
